// Erros de formulário de pessoas: as mensagens do esquema local são chaves de `people:data.validation.*` e os erros de campo do
// servidor (`422 PLT_VALIDATION`, `errors[]` com `field` e `code`) viram mensagens `server:<code>` (`people:common.serverErrors.*`),
// ambas resolvidas por `useFieldMessage`. Os erros de campo que o formulário não exibe continuam na `ProblemAlert`.
import { GiyusApiError } from '@giyus/api-client';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/** Prefixo das mensagens de campo que vieram do servidor (`server:<code>` → `people:common.serverErrors.<code>`). */
export const SERVER_MESSAGE_PREFIX = 'server:';

export interface ServerFieldIssue {
  readonly field: string;
  readonly code: string;
}

export function serverFieldIssues(error: unknown): ServerFieldIssue[] {
  return error instanceof GiyusApiError
    ? error.fieldErrors.map(({ field, code }) => ({ field, code }))
    : [];
}

/** `contacts[0].value` → `contacts.0.value` (caminho do react-hook-form). */
export function toFormPath(field: string): string {
  return field.replace(/\[(\d+)\]/g, '.$1');
}

/**
 * Aplica os erros de campo do servidor no formulário. `mapField` converte o nome do servidor no caminho do formulário
 * (ou `null` para ignorar o campo).
 */
export function applyServerFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  mapField: (field: string) => string | null = (field) => field,
): void {
  for (const issue of serverFieldIssues(error)) {
    const path = mapField(toFormPath(issue.field));
    if (path !== null) {
      setError(path as Path<T>, {
        type: 'server',
        message: `${SERVER_MESSAGE_PREFIX}${issue.code}`,
      });
    }
  }
}

/** Tradutor das mensagens de campo (esquema local e servidor). */
export function useFieldMessage(): (message: string | undefined) => string | undefined {
  const { t } = useTranslation('people');
  return (message) => {
    if (!message) {
      return undefined;
    }
    if (message.startsWith(SERVER_MESSAGE_PREFIX)) {
      const code = message.slice(SERVER_MESSAGE_PREFIX.length);
      return t(`common.serverErrors.${code}`, { defaultValue: code });
    }
    return t(`data.validation.${message}`, { defaultValue: message });
  };
}
